<cs-header>
  <cs-header-title>{{ LL.coach.billing.title() }}</cs-header-title>
  <cs-icon-button header-right icon="refresh"
    [ariaLabel]="LL.coach.billing.refresh()"
    (click)="refreshTappedAsync()"></cs-icon-button>
</cs-header>

<cs-content>
  @if (!loaded && !loadError) {
    <div class="loading-block">
      <cs-skeleton width="220px"></cs-skeleton>
      <cs-skeleton width="180px"></cs-skeleton>
      <cs-skeleton width="140px"></cs-skeleton>
    </div>
  } @else if (loadError && !state) {
    <cs-empty-state
      iconName="cloud-off"
      [title]="LL.coach.billing.loadError.title()"
      [subtext]="LL.coach.billing.loadError.subtext()">
      <cs-button secondary small icon="refresh" (click)="refreshTappedAsync()">
        {{ LL.coach.billing.loadError.retry() }}
      </cs-button>
    </cs-empty-state>
  } @else if (isUnmigrated) {
    <cs-empty-state
      iconName="hourglass"
      [title]="LL.coach.billing.unmigrated.title()"
      [subtext]="LL.coach.billing.unmigrated.subtext()">
    </cs-empty-state>
  } @else if (tierState) {
    <!-- fixed-tier (app-store) coach: current tier + used/cap + Change Plan -->
    <cs-card-list>
      <cs-card>
        <cs-card-content>
          <div class="balance-hero">
            <div class="balance-phrase">{{ getTierName() }}</div>
            <div class="balance-secondary">{{ getTierUsageLine() }}</div>
          </div>
        </cs-card-content>
      </cs-card>

      @if (showChangePlanCta) {
        <cs-card>
          <cs-card-content>
            <div class="action-block">
              <cs-button secondary icon="swap-horizontal"
                [disabled]="isChangingPlan"
                (click)="changePlanAsync()">
                {{ LL.coach.tier.changePlan() }}
              </cs-button>
              <cs-note light>{{ LL.coach.tier.changePlanSubtext() }}</cs-note>
            </div>
          </cs-card-content>
        </cs-card>
      }

      <ng-container *ngTemplateOutlet="historyCards"></ng-container>
    </cs-card-list>
  } @else if (seatState) {
    <!-- Paddle per-seat (web) coach: client-seat count + billed-monthly note.
         Paddle in-app management is not wired (Phase 1 purchase-only), so the
         manage/cancel affordance is an informational note, not a dead button. -->
    <cs-card-list>
      <cs-card>
        <cs-card-content>
          <div class="balance-hero">
            <div class="balance-phrase">{{ getSeatHeading() }}</div>
            <div class="balance-secondary">{{ LL.coach.billing.seat.billedMonthly() }}</div>
            <!-- #4455 — what the next charge will be, captured at the coach's
                 last seat change. The whole row is omitted rather than shown
                 half-filled when that read failed: half a money sentence is
                 worse than none. -->
            @if (getSeatNextPaymentLine(); as nextPayment) {
              <div class="balance-secondary">{{ nextPayment }}</div>
            }
          </div>
        </cs-card-content>
      </cs-card>

      <ng-container *ngTemplateOutlet="historyCards"></ng-container>

      <cs-card>
        <cs-card-content>
          <cs-note light>{{ LL.coach.billing.seat.manageNote() }}</cs-note>
        </cs-card-content>
      </cs-card>
    </cs-card-list>
  } @else {
    <cs-card-list>
      <!-- balance summary: the headline number plus the supporting
           active-slots line and (when relevant) the runout projection -->
      <cs-card>
        <cs-card-content>
          <div class="balance-hero">
            <div class="balance-phrase">{{ getBalancePhrase() }}</div>
            <div class="balance-secondary">{{ getActiveSlotsLine() }}</div>
          </div>
          @if (getRunoutLine()) {
            <div class="balance-runout">{{ getRunoutLine() }}</div>
          }
        </cs-card-content>
      </cs-card>

      <!-- statement history: one row per settled month, or a muted
           empty-state note inside the same card frame -->
      <cs-card>
        <cs-card-title>{{ LL.coach.billing.history.sectionTitle() }}</cs-card-title>
        <cs-card-content>
          @if (history.length > 0) {
            <div class="history-list">
              @for (row of history; track row.period) {
                <div class="history-row">
                  <div class="history-period">{{ getPeriodLabel(row.period) }}</div>
                  <div class="history-consumed">{{ getHistoryConsumedLine(row) }}</div>
                  <div class="history-subtext">{{ getHistoryRowSubtext(row) }}</div>
                </div>
              }
            </div>
          } @else {
            <cs-note light>{{ LL.coach.billing.history.empty() }}</cs-note>
          }
        </cs-card-content>
      </cs-card>
    </cs-card-list>
  }
</cs-content>

<!-- #6768: Clients Over Time and Past Payments, under the plan cards of the
     fixed-tier and Paddle views. They load after the plan state; a failure
     here never blanks the plan cards. -->
<ng-template #historyCards>
  @switch (historyLoad) {
    @case (CoachHistoryLoad.Loading) {
      <cs-card class="history-loading-card">
        <cs-card-content>
          <div class="history-loading">
            <cs-skeleton width="180px"></cs-skeleton>
            <cs-skeleton width="100%" height="180px"></cs-skeleton>
          </div>
        </cs-card-content>
      </cs-card>
    }
    @case (CoachHistoryLoad.Failed) {
      <cs-card class="history-error-card">
        <cs-card-content>
          <div class="action-block">
            <cs-note light>{{ LL.coach.billing.recordedHistory.loadError() }}</cs-note>
            <cs-button secondary small icon="refresh" (click)="loadHistoryAsync()">
              {{ LL.coach.billing.recordedHistory.retry() }}
            </cs-button>
          </div>
        </cs-card-content>
      </cs-card>
    }
    @case (CoachHistoryLoad.Loaded) {
      @if (!hasHistory) {
        <cs-card class="history-empty-card">
          <cs-card-content>
            <cs-note light>{{ LL.coach.billing.recordedHistory.emptyHistory() }}</cs-note>
          </cs-card-content>
        </cs-card>
      } @else {
        <cs-card class="clients-over-time-card">
          <cs-card-title>
            {{ LL.coach.billing.recordedHistory.chartTitle() }}
            <!-- the switch sits beside the title on a wide screen and under it on a phone;
                 both copies are written out, and the page styles show one. Written here
                 rather than through a template, because content projection only sees an
                 element written directly inside the title -->
            <cs-segment title-action class="history-range-switch beside-title" role="group" [attr.aria-label]="LL.coach.billing.recordedHistory.rangeAria()" [selection]="historyRange"
              (selectionChange)="onHistoryRangeChanged($event)">
              <cs-segment-button [value]="CoachHistoryRange.SixMonths">{{ LL.coach.billing.recordedHistory.range6() }}</cs-segment-button>
              <cs-segment-button [value]="CoachHistoryRange.TwelveMonths">{{ LL.coach.billing.recordedHistory.range12() }}</cs-segment-button>
            </cs-segment>
          </cs-card-title>
          <cs-card-content>
            <div class="history-chart-block">
              <cs-segment class="history-range-switch under-title" role="group" [attr.aria-label]="LL.coach.billing.recordedHistory.rangeAria()" [selection]="historyRange"
                (selectionChange)="onHistoryRangeChanged($event)">
                <cs-segment-button [value]="CoachHistoryRange.SixMonths">{{ LL.coach.billing.recordedHistory.range6() }}</cs-segment-button>
                <cs-segment-button [value]="CoachHistoryRange.TwelveMonths">{{ LL.coach.billing.recordedHistory.range12() }}</cs-segment-button>
              </cs-segment>
              <cs-coach-clients-chart [bars]="clientBars"></cs-coach-clients-chart>
              @if (chartCap !== undefined) {
                <cs-note light>{{ LL.coach.billing.recordedHistory.capNote({ cap: chartCap }) }}</cs-note>
              } @else {
                <cs-note light>{{ LL.coach.billing.recordedHistory.mostClientsNote() }}</cs-note>
              }
            </div>
          </cs-card-content>
        </cs-card>

        @if (charges.length === 0) {
          <cs-card class="past-payments-card">
            <cs-card-title>{{ LL.coach.billing.recordedHistory.paymentsTitle() }}</cs-card-title>
            <cs-card-content>
              <cs-note light>{{ LL.coach.billing.recordedHistory.emptyPayments() }}</cs-note>
            </cs-card-content>
          </cs-card>
        } @else if (charges.length > foldedChargeCount) {
          <!-- a foldable card shows only its collapsed and expanded slots, so both carry the rows -->
          <cs-card class="past-payments-card" [foldable]="true" [(expanded)]="paymentsExpanded">
            <cs-card-title>{{ LL.coach.billing.recordedHistory.paymentsTitle() }}</cs-card-title>
            <div collapsed class="payments-slot">
              <ng-container *ngTemplateOutlet="chargeRows; context: { $implicit: foldedCharges }"></ng-container>
            </div>
            <div expanded class="payments-slot">
              <ng-container *ngTemplateOutlet="chargeRows; context: { $implicit: charges }"></ng-container>
            </div>
          </cs-card>
        } @else {
          <cs-card class="past-payments-card">
            <cs-card-title>{{ LL.coach.billing.recordedHistory.paymentsTitle() }}</cs-card-title>
            <cs-card-content>
              <div class="payments-slot">
                <ng-container *ngTemplateOutlet="chargeRows; context: { $implicit: charges }"></ng-container>
              </div>
            </cs-card-content>
          </cs-card>
        }
      }
    }
  }
</ng-template>

<ng-template #chargeRows let-rows>
  <div class="history-list">
    @for (row of rows; track row.chargeId ?? row.at_utc) {
      <div class="history-row charge-row">
        <div class="charge-row-inner">
          <div class="charge-text">
            <div class="history-period">{{ getChargeDate(row) }}</div>
            <div class="history-consumed charge-line">
              <span>{{ getChargeLine(row) }}</span>
              @if (row.isTestPurchase) {
                <cs-badge [label]="LL.coach.billing.recordedHistory.testBadge()" color="gray"></cs-badge>
              }
            </div>
          </div>
          @if (row.paymentRoute === CoachHistoryRoute.Paddle) {
            <cs-button tertiary class="invoice-button" (click)="openInvoiceAsync($event, row)">
              {{ LL.coach.billing.recordedHistory.invoice() }}
            </cs-button>
          }
        </div>
      </div>
    }
  </div>
  @for (store of receiptStores; track store) {
    <cs-note light class="receipts-note">{{ getReceiptsLine(store) }}</cs-note>
  }
</ng-template>
